import { useState, useEffect } from "react";
import { getIdByUrl } from "../../commons/constants/helpers";
import "./VideoItem.css";

interface IVideoItem {
	url: string;
	title: string;
	md?: number;
}

export default function VideoItem({ url, title }: IVideoItem) {
	const [isVideoOpen, setIsVideoOpen] = useState(false);
	const [thumbnailUrl, setThumbnailUrl] = useState("");
	const youtubeVideoId = getIdByUrl(url);

	// Load thumbnail with fallback strategy
	useEffect(() => {
		const loadThumbnail = async () => {
			const thumbnailOptions = [
				`https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`,
				`https://img.youtube.com/vi/${youtubeVideoId}/sddefault.jpg`,
				`https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`,
			];

			for (const thumbnailOption of thumbnailOptions) {
				try {
					const response = await fetch(thumbnailOption, { method: "HEAD" });
					if (response.ok) {
						// Check if it's not the default gray image
						const img = new Image();
						img.onload = () => {
							// YouTube's default gray image is 120x90, real thumbnails are larger
							if (img.width > 120) {
								setThumbnailUrl(thumbnailOption);
							}
						};
						img.src = thumbnailOption;
						break;
					}
				} catch (error) {
					continue;
				}
			}

			// Final fallback
			if (!thumbnailUrl) {
				setThumbnailUrl(`https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`);
			}
		};

		loadThumbnail();
	}, [youtubeVideoId, thumbnailUrl]);

	useEffect(() => {
		// Prevent body scroll when modal is open
		if (isVideoOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isVideoOpen]);

	const handleVideoClick = () => {
		setIsVideoOpen(true);
	};

	const handleCloseVideo = () => {
		setIsVideoOpen(false);
	};

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			handleCloseVideo();
		}
	};

	return (
		<>
			<div className="video-card" onClick={handleVideoClick}>
				<div className="video-thumbnail-container">
					{thumbnailUrl ? (
						<img
							src={thumbnailUrl}
							alt={title}
							className="video-thumbnail"
							loading="lazy"
						/>
					) : (
						<div className="video-thumbnail-loading">
							<div className="loading-spinner" />
						</div>
					)}
					<div className="video-overlay" />
					<div className="play-button-container">
						<div className="play-button" />
					</div>
				</div>
				<div className="video-info">
					<h3 className="video-title">{title}</h3>
				</div>
			</div>

			{isVideoOpen && (
				<div className="video-modal-overlay" onClick={handleOverlayClick}>
					<div className="video-modal-content">
						<button
							className="video-modal-close"
							onClick={handleCloseVideo}
							aria-label="Close video"
						>
							×
						</button>
						<iframe
							className="video-modal-iframe"
							title={title}
							src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
						/>
					</div>
				</div>
			)}
		</>
	);
}
